/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { MdDelete, MdEdit } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { formatDistance } from 'date-fns';

import {
    DialogContentText,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@material-ui/core';

import DataTable from '~/components/DataTable';
import LocaleMessage from '~/components/LocaleMessage';
import PageContent from '~/components/PageContent';
import SimpleDialog from '~/components/SimpleDialog';

import api from '~/services/pluginbot-api';
import { expireSession } from '~/store/modules/auth/actions';
import getDateLocale from '~/util/GetDateLocale';

export default function CardsDashboards() {
    const dispatch = useDispatch();
    const _settings = useSelector(state => state.settings || null);
    const date_loc = getDateLocale(_settings);
    const [isLoading, setIsLoading] = useState(true);
    const [cards, setCards] = useState([]);

    // Modals state
    const [formOpen, setFormOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [cardName, setCardName] = useState('');

    function requestError(error) {
        if (error && error.response) {
            const code = error.response.data && error.response.data.code;
            const message = code ? (
                <LocaleMessage msg={`errors.${code}`} />
            ) : (
                <LocaleMessage msg="errors.unknown" />
            );
            toast.error(message);
            const { status } = error.response;
            if (status === 401) {
                dispatch(expireSession());
            }
        } else if (error && error.request) {
            toast.error(<LocaleMessage msg="errors.request" />);
        } else {
            toast.error(<LocaleMessage msg="errors.unknown" />);
        }
        setIsLoading(false);
    }

    async function loadItems() {
        try {
            setIsLoading(true);
            const response = await api.get('admin/cards_dashboards');
            if (response.status === 200) {
                if (response.data) {
                    const data = response.data.map(c => {
                        const updated = new Date(c.updated_at || c.createdAt);
                        return {
                            id: c.uid, // DataTable expects id field for row actions/clicks
                            uid: c.uid,
                            name:
                                c.name &&
                                c.name.indexOf('dashboard.card.') === 0 ? (
                                    <LocaleMessage msg={c.name} />
                                ) : (
                                    c.name
                                ),
                            rawName: c.name,
                            updated: formatDistance(updated, new Date(), {
                                addSuffix: true,
                                locale: date_loc,
                            }),
                            updated_timestamp: updated.toISOString(),
                        };
                    });
                    setCards(data);
                }
            } else {
                toast.error(<LocaleMessage msg="errors.permission" />);
            }
            setIsLoading(false);
        } catch (err) {
            requestError(err);
        }
    }

    useEffect(() => {
        loadItems();
    }, [date_loc]);

    function handleOpenAdd() {
        setSelectedCard(null);
        setCardName('');
        setFormOpen(true);
    }

    function handleOpenEdit(event, cardId) {
        if (event) event.preventDefault();
        const card = cards.find(c => c.uid === cardId);
        if (card) {
            setSelectedCard(card);
            setCardName(card.rawName);
            setFormOpen(true);
        }
    }

    function handleOpenDelete(event, cardId) {
        if (event) event.preventDefault();
        setSelectedCard(cards.find(c => c.uid === cardId) || null);
        setConfirmOpen(true);
    }

    async function handleSaveCard(event) {
        if (event) event.preventDefault();
        if (!cardName || cardName.trim() === '') {
            toast.error(<LocaleMessage msg="validation.required" />);
            return;
        }

        setIsLoading(true);
        try {
            if (selectedCard) {
                // Edit
                await api.put(`admin/cards_dashboards/${selectedCard.uid}`, {
                    name: cardName,
                });
                toast.success(
                    <LocaleMessage msg="page.cards_dashboards.save_success" />
                );
            } else {
                // Add
                await api.post('admin/cards_dashboards', {
                    name: cardName,
                });
                toast.success(
                    <LocaleMessage msg="page.cards_dashboards.create_success" />
                );
            }
            setFormOpen(false);
            loadItems();
        } catch (err) {
            requestError(err);
        }
    }

    async function handleDeleteCard() {
        if (!selectedCard) return;
        setIsLoading(true);
        setConfirmOpen(false);
        try {
            await api.delete(`admin/cards_dashboards/${selectedCard.uid}`);
            toast.success(
                <LocaleMessage msg="page.cards_dashboards.delete_success" />
            );
            loadItems();
        } catch (err) {
            requestError(err);
        }
    }

    function buildListView() {
        const headCells = [
            { id: 'name', label: <LocaleMessage msg="table.headers.name" /> },
            {
                id: 'updated',
                label: <LocaleMessage msg="table.headers.updated" />,
                order_by: 'updated_timestamp',
            },
        ];

        const rowActions = [
            {
                id: 'edit',
                label: <LocaleMessage msg="button.edit" />,
                icon: <MdEdit />,
                action: (event, id) => handleOpenEdit(event, id),
            },
            {
                id: 'delete',
                label: <LocaleMessage msg="button.delete" />,
                icon: <MdDelete />,
                action: (event, id) => handleOpenDelete(event, id),
            },
        ];

        return (
            <div style={{ minHeight: '150px', width: '100%', padding: '15px' }}>
                <DataTable
                    headerColumns={headCells}
                    data={cards}
                    orderColumn="name"
                    rowActions={rowActions}
                />
            </div>
        );
    }

    return (
        <PageContent
            title={<LocaleMessage msg="page.cards_dashboards.title" />}
            loading={isLoading}
        >
            <>
                <div
                    className="body-top-controls mb-3"
                    style={{ marginBottom: '20px' }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenAdd}
                    >
                        <LocaleMessage msg="page.cards_dashboards.button.new" />
                    </Button>
                </div>
                {buildListView()}

                {/* Save Card Modal */}
                <Dialog
                    open={formOpen}
                    onClose={() => setFormOpen(false)}
                    aria-labelledby="form-dialog-title"
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle id="form-dialog-title">
                        {selectedCard ? (
                            <LocaleMessage msg="page.cards_dashboards.edit_title" />
                        ) : (
                            <LocaleMessage msg="page.cards_dashboards.create_title" />
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            label={<LocaleMessage msg="table.headers.name" />}
                            type="text"
                            fullWidth
                            value={cardName}
                            onChange={e => setCardName(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setFormOpen(false)}
                            color="secondary"
                        >
                            <LocaleMessage msg="button.cancel" />
                        </Button>
                        <Button onClick={handleSaveCard} color="primary">
                            <LocaleMessage msg="button.save" />
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Delete Dialog */}
                <SimpleDialog
                    open={confirmOpen}
                    onClose={() => setConfirmOpen(false)}
                    title={
                        <LocaleMessage msg="page.cards_dashboards.delete_title" />
                    }
                    content={
                        <DialogContentText>
                            <LocaleMessage msg="message.undone.content" />
                        </DialogContentText>
                    }
                    actions={[
                        {
                            key: 'cancel',
                            onClick: () => setConfirmOpen(false),
                            label: <LocaleMessage msg="button.cancel" />,
                        },
                        {
                            key: 'remove',
                            onClick: handleDeleteCard,
                            label: <LocaleMessage msg="button.delete" />,
                        },
                    ]}
                />
            </>
        </PageContent>
    );
}
